import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Share2, Trash2, Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { galleryAPI, getImageUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import UserLayout from "../components/UserLayout";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const GalleryPage = () => {
    const { t } = useLanguage();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [deleting, setDeleting] = useState(null);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const response = await galleryAPI.getAll(0, 100);
            setImages(response.data);
        } catch (error) {
            toast.error(t('gallery_load_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setDeleting(id);
        try {
            await galleryAPI.delete(id);
            setImages(images.filter(img => img.id !== id));
            toast.success(t('image_deleted'));
            setSelectedImage(null);
        } catch (error) {
            toast.error(t('delete_error'));
        } finally {
            setDeleting(null);
        }
    };

    const handleDownload = async (imageUrl, fileName) => {
        try {
            const fullUrl = getImageUrl(imageUrl);
            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'generated-image.png';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success(t('download_started'));
        } catch (error) {
            toast.error(t('download_error'));
        }
    };

    const handleShare = async (imageUrl) => {
        try {
            const fullUrl = getImageUrl(imageUrl);
            if (navigator.share) {
                await navigator.share({
                    title: 'Generated Image - mangoplus.ai',
                    url: fullUrl
                });
            } else {
                await navigator.clipboard.writeText(fullUrl);
                toast.success(t('link_copied'));
            }
        } catch (error) {
            console.error("Share error:", error);
        }
    };

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="gallery-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">{t('gallery')}</h1>
                        <p className="text-gray-600 mt-1">{t('gallery_subtitle')}</p>
                    </div>
                    <span className="text-sm text-gray-500">{images.length} {t('images')}</span>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="aspect-square rounded-2xl bg-gray-100 animate-pulse" />
                        ))}
                    </div>
                ) : images.length > 0 ? (
                    <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
                        {images.map((image, index) => (
                            <motion.div
                                key={image.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="break-inside-avoid group relative rounded-2xl overflow-hidden bg-gray-100 cursor-pointer"
                                onClick={() => setSelectedImage(image)}
                                data-testid={`gallery-item-${image.id}`}
                            >
                                <img
                                    src={getImageUrl(image.thumbnail_url || image.image_url)}
                                    alt="Generated"
                                    className="w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="rounded-full"
                                        onClick={(e) => { e.stopPropagation(); handleDownload(image.image_url, `image-${image.id}.png`); }}
                                        data-testid={`download-${image.id}`}
                                    >
                                        <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="rounded-full"
                                        onClick={(e) => { e.stopPropagation(); handleShare(image.image_url); }}
                                        data-testid={`share-${image.id}`}
                                    >
                                        <Share2 className="w-4 h-4" />
                                    </Button>
                                </div>
                                {image.status === "processing" && (
                                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20">
                        <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{t('gallery_empty')}</h3>
                        <p className="text-gray-500">{t('gallery_empty_hint')}</p>
                    </div>
                )}

                {/* Image Detail Dialog */}
                <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
                    <DialogContent className="max-w-4xl">
                        <DialogHeader>
                            <DialogTitle>{t('image_detail')}</DialogTitle>
                        </DialogHeader>
                        {selectedImage && (
                            <div className="space-y-4">
                                <div className="rounded-xl overflow-hidden bg-gray-100">
                                    <img
                                        src={getImageUrl(selectedImage.image_url)}
                                        alt="Generated"
                                        className="w-full max-h-[60vh] object-contain"
                                    />
                                </div>
                                <div className="grid md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">{t('type')}:</span>
                                        <span className="ml-2 font-medium capitalize">{selectedImage.photo_type?.replace('_', ' ')}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t('tokens')}:</span>
                                        <span className="ml-2 font-medium">{selectedImage.tokens_used}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t('ai_provider')}:</span>
                                        <span className="ml-2 font-medium capitalize">{selectedImage.ai_provider?.replace('_', ' ')}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">{t('status')}:</span>
                                        <span className={`ml-2 font-medium capitalize ${selectedImage.status === 'completed' ? 'text-green-600' : selectedImage.status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`}>
                                            {selectedImage.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Button
                                        onClick={() => handleDownload(selectedImage.image_url, `image-${selectedImage.id}.png`)}
                                        className="flex-1 rounded-xl"
                                        data-testid="modal-download-btn"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        {t('download')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => handleShare(selectedImage.image_url)}
                                        className="rounded-xl"
                                        data-testid="modal-share-btn"
                                    >
                                        <Share2 className="w-4 h-4 mr-2" />
                                        {t('share')}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={() => handleDelete(selectedImage.id)}
                                        disabled={deleting === selectedImage.id}
                                        className="rounded-xl"
                                        data-testid="modal-delete-btn"
                                    >
                                        {deleting === selectedImage.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default GalleryPage;
